/**
 * useChatAttachments Hook
 *
 * Manages the lifecycle of inline chat attachments:
 * validation → presigned URL → S3 upload with progress → ready refs for send.
 *
 * Shared across all three chat surfaces (AI Chat, Tender Chat, Draft AI Panel).
 */

import { useState, useCallback, useRef } from 'react'
import { api, uploadFileToS3 } from '@/lib/api'
import {
  validateChatAttachment,
  validateChatAttachmentMeta,
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_TOTAL_SIZE,
} from '@/lib/assistant-attachment-validation'
import type { ChatAttachmentState, ChatAttachmentRef } from '@/types/assistant-attachments'
import type { SharePointStagedItem } from '@/components/SharePointBrowser'

// ===================
// Types
// ===================

interface UploadUrlResponse {
  attachmentId: string
  uploadUrl: string
  s3Key: string
}

interface SharePointChatAttachmentResponse {
  attachmentId: string
  s3Key: string
  filename: string
  mimeType: string
  size: number
}

// ===================
// Hook
// ===================

export function useAssistantAttachments() {
  const [attachments, setAttachments] = useState<ChatAttachmentState[]>([])
  const abortControllers = useRef<Map<string, AbortController>>(new Map())

  // ----- Computed -----

  const isUploading = attachments.some((a) => a.status === 'uploading')
  const hasErrors = attachments.some((a) => a.status === 'error')
  const hasAttachments = attachments.length > 0
  const totalSize = attachments.reduce((sum, a) => sum + a.size, 0)
  const canAttach = attachments.length < MAX_CHAT_ATTACHMENTS && totalSize < MAX_CHAT_TOTAL_SIZE

  // ----- Upload a single file -----

  const uploadFile = useCallback(async (att: ChatAttachmentState) => {
    // The S3 PUT path is upload-only — SharePoint-sourced attachments carry no
    // local File and are ingested server-side instead.
    const file = att.file
    if (!file) return

    const requestUploadUrl = async () => {
      const response = await api.post<UploadUrlResponse>(
        '/chat/attachments/upload-url',
        { filename: att.filename, mimeType: att.mimeType, size: att.size }
      )
      // Update state with server-assigned IDs
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === att.id
            ? { ...a, attachmentId: response.attachmentId, s3Key: response.s3Key }
            : a
        )
      )
      return response.uploadUrl
    }

    const putToS3 = (uploadUrl: string) =>
      uploadFileToS3(uploadUrl, file, (progress) => {
        setAttachments((prev) =>
          prev.map((a) => (a.id === att.id ? { ...a, progress } : a))
        )
      })

    try {
      const uploadUrl = await requestUploadUrl()

      try {
        await putToS3(uploadUrl)
      } catch {
        // One retry with a FRESH presigned URL (G11): the URL is only valid for
        // 10 minutes, so a slow upload can expire mid-flight — replaying the
        // stale URL would just fail again.
        await putToS3(await requestUploadUrl())
      }

      // Mark ready
      setAttachments((prev) =>
        prev.map((a) => (a.id === att.id ? { ...a, status: 'ready' as const, progress: 100 } : a))
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed'
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === att.id ? { ...a, status: 'error' as const, error: message } : a
        )
      )
    } finally {
      abortControllers.current.delete(att.id)
    }
  }, [])

  // ----- Add files -----

  const addFiles = useCallback(
    (files: File[]) => {
      const currentCount = attachments.length
      const currentSize = totalSize

      const newAttachments: ChatAttachmentState[] = []
      let accumulatedSize = currentSize

      for (const file of files) {
        // Count limit
        if (currentCount + newAttachments.length >= MAX_CHAT_ATTACHMENTS) break

        // Total size limit
        if (accumulatedSize + file.size > MAX_CHAT_TOTAL_SIZE) continue

        // Per-file validation
        const validation = validateChatAttachment(file)
        const id = crypto.randomUUID()
        const abortController = new AbortController()
        abortControllers.current.set(id, abortController)

        if (!validation.valid) {
          newAttachments.push({
            id,
            file,
            source: 'upload' as const,
            filename: file.name,
            mimeType: file.type,
            size: file.size,
            status: 'error',
            progress: 0,
            error: validation.error ?? 'Invalid file',
          })
          continue
        }

        accumulatedSize += file.size
        newAttachments.push({
          id,
          file,
          source: 'upload' as const,
          filename: file.name,
          mimeType: file.type,
          size: file.size,
          status: 'uploading',
          progress: 0,
          abortController,
        })
      }

      setAttachments((prev) => [...prev, ...newAttachments])

      // Start uploads for valid files
      for (const att of newAttachments) {
        if (att.status === 'uploading') {
          void uploadFile(att)
        }
      }
    },
    [attachments.length, totalSize, uploadFile]
  )

  // ----- Add SharePoint items -----

  /**
   * Server-side ingest of one picked SharePoint item.
   *
   * There is no local `File` and no S3 PUT from the browser — the backend
   * streams the file out of Graph and into our bucket, then hands back the same
   * `attachmentId`/`s3Key` refs an uploaded attachment carries.
   */
  const fetchSharePointAttachment = useCallback(
    async (localId: string, driveId: string, itemId: string) => {
      try {
        const res = await api.post<SharePointChatAttachmentResponse>(
          '/sharepoint/chat-attachment',
          { driveId, itemId }
        )
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === localId
              ? {
                  ...a,
                  status: 'ready' as const,
                  progress: 100,
                  attachmentId: res.attachmentId,
                  s3Key: res.s3Key,
                }
              : a
          )
        )
      } catch (err) {
        // Transport-level failures get friendly copy; real API messages pass
        // through. The bare-message regex only catches an unwrapped TypeError —
        // `apiRequest` normally rethrows an ApiRequestError whose message is its
        // own generic copy, so the reliable signals are the code and the status:
        //   - NETWORK_ERROR  → what apiRequest wraps 'Failed to fetch' into
        //   - 502/503/504    → the API GW 29s cutoff, whose message is the bare
        //                      statusText ('Gateway Timeout')
        // Duck-typed rather than `instanceof ApiRequestError` so the hook does
        // not depend on the class surviving a module mock.
        const { code, statusCode } = (err ?? {}) as { code?: string; statusCode?: number }
        const isTransportFailure =
          code === 'NETWORK_ERROR' ||
          (statusCode !== undefined && statusCode >= 502 && statusCode <= 504) ||
          !(err instanceof Error) ||
          !err.message ||
          /failed to fetch|networkerror|load failed/i.test(err.message)

        const message = isTransportFailure
          ? 'SharePoint is taking too long — try again.'
          : err.message
        setAttachments((prev) =>
          prev.map((a) => (a.id === localId ? { ...a, status: 'error' as const, error: message } : a))
        )
      }
    },
    []
  )

  const addSharePointItems = useCallback(
    (driveId: string, items: SharePointStagedItem[]) => {
      const slots = MAX_CHAT_ATTACHMENTS - attachments.length
      let accumulatedSize = totalSize
      const newAttachments: ChatAttachmentState[] = []

      for (const item of items.slice(0, Math.max(0, slots))) {
        const id = crypto.randomUUID()
        const { mimeType } = item

        // Defence in depth — the picker already disables ineligible rows, but a
        // stale selection or a Graph item with no MIME must never reach the API.
        if (mimeType === null) {
          newAttachments.push({
            id,
            source: 'sharepoint' as const,
            filename: item.name,
            mimeType: '',
            size: item.size,
            status: 'error',
            progress: 0,
            error: 'This file type is not supported for chat attachments',
          })
          continue
        }

        const validation = validateChatAttachmentMeta(item.name, mimeType, item.size)

        if (!validation.valid || accumulatedSize + item.size > MAX_CHAT_TOTAL_SIZE) {
          newAttachments.push({
            id,
            source: 'sharepoint' as const,
            filename: item.name,
            mimeType,
            size: item.size,
            status: 'error',
            progress: 0,
            error: validation.valid
              ? 'Total attachment size exceeds 25MB'
              : validation.error ?? 'Invalid file',
          })
          continue
        }

        accumulatedSize += item.size
        newAttachments.push({
          id,
          source: 'sharepoint' as const,
          filename: item.name,
          mimeType,
          size: item.size,
          status: 'uploading',
          progress: 0,
        })
        // Fired before `setAttachments` installs the entry, same as `addFiles`
        // does with `uploadFile`. The functional `setAttachments` callbacks
        // inside run after React applies the install, so the `.map` finds it.
        // Removal before completion just makes the `.map` a no-op — that is the
        // late-success-ignored contract (the api client has no AbortSignal).
        void fetchSharePointAttachment(id, driveId, item.itemId)
      }

      setAttachments((prev) => [...prev, ...newAttachments])
    },
    [attachments.length, totalSize, fetchSharePointAttachment]
  )

  // ----- Remove -----

  const removeAttachment = useCallback((id: string) => {
    const controller = abortControllers.current.get(id)
    if (controller) {
      controller.abort()
      abortControllers.current.delete(id)
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }, [])

  // ----- Clear -----

  const clearAttachments = useCallback(() => {
    for (const controller of abortControllers.current.values()) {
      controller.abort()
    }
    abortControllers.current.clear()
    setAttachments([])
  }, [])

  // ----- Restore -----

  /**
   * Put a previously-cleared set of attachments back on the composer.
   *
   * The composer clears optimistically on send; when the turn then fails, the
   * user is left having to re-pick files from disk. Restoring is safe because
   * `ready` attachments are already uploaded — `clearAttachments` only drops
   * local state, it does not delete anything from S3 — so the same
   * `attachmentId`/`s3Key` refs stay valid for the retry.
   *
   * Only `ready` items are restored: an in-flight upload had its controller
   * aborted by `clearAttachments` and cannot be resumed, and a failed one has
   * nothing to send.
   */
  const restoreAttachments = useCallback((items: ChatAttachmentState[]) => {
    const restorable = items.filter((a) => a.status === 'ready')
    if (restorable.length === 0) return
    setAttachments((prev) => {
      // Guard against clobbering anything the user attached while the failed
      // turn was in flight.
      const existing = new Set(prev.map((a) => a.id))
      return [...prev, ...restorable.filter((a) => !existing.has(a.id))]
    })
  }, [])

  // ----- Get ready refs for sending -----

  const getReadyRefs = useCallback((): ChatAttachmentRef[] => {
    return attachments
      .filter((a): a is ChatAttachmentState & { attachmentId: string; s3Key: string } =>
        a.status === 'ready' && !!a.attachmentId && !!a.s3Key
      )
      .map((a) => ({
        attachmentId: a.attachmentId,
        s3Key: a.s3Key,
        filename: a.filename,
        mimeType: a.mimeType,
        size: a.size,
      }))
  }, [attachments])

  return {
    attachments,
    addFiles,
    addSharePointItems,
    removeAttachment,
    clearAttachments,
    restoreAttachments,
    getReadyRefs,
    isUploading,
    hasErrors,
    hasAttachments,
    canAttach,
    totalSize,
  }
}
